/**
 * The ficha de produção as the stored quotation carries it (`productionSheet`,
 * written by `quotationToSheet` in orchestration), and the little formatting
 * the screen needs to print it the way the paper does.
 *
 * Reference, not review: nothing here is compared with the receita.
 */

export interface IngredientSheet {
  ingredientId: string
  /*
   * The stored ficha also carries each line's lot, its expiry and supplier.
   * The screen no longer reads them (meeting of 2026-09-23, #467): the
   * pharmacist who confers does not check them, the person weighing does.
   */
  /**
   * `FC12110.QTREAL`/`UNIDAPRD`: how much of the keyed product is weighed for
   * the whole requisição, in its stock unit -- the dose times the pote's
   * quantity, already multiplied by the dilution and equivalence (measured on
   * production, #467). So a diluted active weighs more than its dose says.
   */
  weighed?: { amount: number; unit: string }
  potency?: number
  dilution?: number
  equivalence?: number
  density?: number
  /**
   * The product's "Observações de Ficha" for the pote's form: the middle grid
   * of the "Observações" tab of the ERP's product screen (#616). Absent on a
   * ficha stored before it was read; one stored between #498 and #616 holds
   * an incompatibility note instead, until the verification is read again.
   */
  observation?: string
}

export interface PreparationSheet {
  preparationId: string
  packaging?: string
  registration?: string
  overfill?: number
  validUntil?: string
  ingredients: IngredientSheet[]
}

export interface ProductionSheet {
  available: boolean
  preparations: PreparationSheet[]
}

/** The ficha on a stored quotation, or undefined when none was ever read. */
export function parseProductionSheet(quotation: unknown): ProductionSheet | undefined {
  if (quotation === null || typeof quotation !== 'object') return undefined
  const sheet = (quotation as { productionSheet?: unknown }).productionSheet
  if (sheet === null || typeof sheet !== 'object') return undefined
  const { available, preparations } = sheet as Partial<ProductionSheet>
  if (typeof available !== 'boolean' || !Array.isArray(preparations)) return undefined
  return { available, preparations }
}

/** `2027-11-14` as the ficha prints it, `14/11/2027`. */
export function formatDay(day: string | undefined): string | undefined {
  const match = day === undefined ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(day)
  return match === null ? undefined : `${match[3]}/${match[2]}/${match[1]}`
}

/** A number the way the ficha writes it: comma decimals, no trailing zeros. */
export function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
}

/**
 * The factors that turned the dose into what is weighed, in the ficha's
 * order. A dilution of 1 and an equivalence of 1 change nothing, so they are
 * left out rather than printed as noise.
 */
export function lineFactors(line: IngredientSheet): string[] {
  const factors: string[] = []
  if (line.potency !== undefined) factors.push(`Teor ${formatNumber(line.potency)}`)
  if (line.dilution !== undefined && line.dilution !== 1)
    factors.push(`Diluição 1:${formatNumber(line.dilution)}`)
  if (line.equivalence !== undefined && line.equivalence !== 1)
    factors.push(`Equivalência ${formatNumber(line.equivalence)}`)
  if (line.density !== undefined) factors.push(`Densidade ${formatNumber(line.density)}`)
  return factors
}
